import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Trash2, ChevronDown, ChevronUp, RotateCcw, Camera } from 'lucide-react';
import { FotoThumbnail } from '../../components/shared/FotoThumbnail';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Skeleton } from '../../components/ui/Skeleton';
import { StatusBadge } from '../../components/ui/Badge';
import { usePlanillaDetail, useCreateDescuento, useDeleteDescuento, usePassToAdmin, useReturnFromCompras } from '../../hooks/usePlanillas';
import type { PlanillaDetailDTO, DescuentoDTO } from '../../hooks/usePlanillas';
import { toast } from '../../components/ui/Toast';

export function ComprasReviewPage() {
  const { planillaId } = useParams<{ planillaId: string }>();
  const navigate = useNavigate();
  const { data: planilla, isLoading } = usePlanillaDetail(planillaId ?? '');
  const passToAdmin = usePassToAdmin(planillaId ?? '');
  const returnFromCompras = useReturnFromCompras(planillaId ?? '');
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnComment, setReturnComment] = useState('');

  const handleReturnToResidente = async () => {
    if (returnComment.length < 10) {
      toast('error', 'El comentario debe tener al menos 10 caracteres');
      return;
    }
    try {
      await returnFromCompras.mutateAsync(returnComment);
      setShowReturnModal(false);
      toast('success', 'Planilla devuelta al residente para corrección');
      navigate('/planillas');
    } catch {
      toast('error', 'Error al devolver planilla');
    }
  };

  const handlePassToAdmin = async () => {
    try {
      await passToAdmin.mutateAsync();
      toast('success', `Planilla N°${planilla?.numero} enviada a revisión administrativa`);
      navigate('/planillas');
    } catch {
      toast('error', 'Error al pasar planilla a revisión');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 fade-up">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!planilla) {
    return <div className="p-4 text-center text-red-600">Planilla no encontrada</div>;
  }

  // Group details and descuentos by maestro
  const maestroIds = [...new Set([
    ...planilla.details.map((d) => d.maestro_id),
    ...planilla.descuentos.map((d) => d.maestro_id),
  ])];

  const maestroMap = new Map<string, string>();
  for (const d of planilla.details) {
    maestroMap.set(d.maestro_id, d.maestro_nombre);
  }
  for (const m of planilla.maestros) {
    maestroMap.set(m.maestro_id, m.nombre);
  }

  return (
    <div className="space-y-4 fade-up pb-24">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-brand-dark">
            Revisión Descuentos — Planilla N°{planilla.numero}
          </h1>
          <p className="text-xs text-gray-500">
            {planilla.project_nombre} · {planilla.fecha_desde} — {planilla.fecha_hasta}
          </p>
        </div>
        <StatusBadge entity="planilla" status={planilla.estado} />
      </div>

      {/* Maestro sections */}
      {maestroIds.map((maestroId) => (
        <MaestroSection
          key={maestroId}
          maestroId={maestroId}
          maestroNombre={maestroMap.get(maestroId) ?? 'Maestro'}
          details={planilla.details.filter((d) => d.maestro_id === maestroId)}
          descuentos={planilla.descuentos.filter((d) => d.maestro_id === maestroId)}
          planillaId={planillaId!}
          isEditable={planilla.estado === 'pendiente_compras'}
        />
      ))}

      {/* Totals summary */}
      <Card className="p-4">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Total rubros</span>
            <span className="font-medium">${planilla.totals.total_rubros.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Total extras</span>
            <span className="font-medium">${planilla.totals.total_extras.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-red-600">
            <span>Total descuentos</span>
            <span className="font-medium">-${planilla.totals.total_descuentos.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between border-t pt-2 font-bold text-brand-dark">
            <span>Total neto</span>
            <span>${planilla.totals.total_neto.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      </Card>

      {/* Photo gallery */}
      {planilla.fotos.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Fotos de evidencia ({planilla.fotos.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {planilla.fotos.map((f) => (
              <FotoThumbnail key={f.id} s3Key={f.s3_key} filename={f.filename} />
            ))}
          </div>
        </Card>
      )}

      {/* Action buttons */}
      {planilla.estado === 'pendiente_compras' && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20">
          <div className="max-w-4xl mx-auto flex gap-3 justify-end">
            <Button
              variant="danger"
              onClick={() => setShowReturnModal(true)}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Solicitar correcciones
            </Button>
            <Button
              onClick={handlePassToAdmin}
              isLoading={passToAdmin.isPending}
            >
              {planilla.descuentos.some((d) => d.tipo === 'herramienta')
                ? 'Guardar descuentos y pasar a revisión'
                : 'Sin cambios — Pasar a revisión'}
            </Button>
          </div>
        </div>
      )}

      {/* Return to residente modal */}
      <Modal isOpen={showReturnModal} onClose={() => setShowReturnModal(false)} title="Solicitar correcciones al residente">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Indique qué debe corregir el residente (mínimo 10 caracteres):</p>
          <textarea
            value={returnComment}
            onChange={(e) => setReturnComment(e.target.value)}
            rows={4}
            placeholder="Describir qué debe corregir el residente..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none"
          />
          <p className="text-xs text-gray-400">{returnComment.length}/10 caracteres mínimo</p>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowReturnModal(false)}>Cancelar</Button>
            <Button
              variant="danger"
              onClick={handleReturnToResidente}
              isLoading={returnFromCompras.isPending}
              disabled={returnComment.length < 10}
            >
              Devolver al residente
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Maestro Section ─────────────────────────────────────────────────

function MaestroSection({
  maestroId,
  maestroNombre,
  details,
  descuentos,
  planillaId,
  isEditable,
}: {
  maestroId: string;
  maestroNombre: string;
  details: PlanillaDetailDTO['details'];
  descuentos: DescuentoDTO[];
  planillaId: string;
  isEditable: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const subtotalRubros = details.reduce((sum, d) => sum + d.subtotal, 0);
  const totalDescuentos = descuentos.reduce((sum, d) => sum + d.monto, 0);
  const neto = subtotalRubros - totalDescuentos;

  return (
    <Card className="overflow-hidden">
      <button
        className="w-full flex items-center justify-between p-4 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div>
          <p className="text-sm font-semibold text-gray-800">{maestroNombre}</p>
          <p className="text-xs text-gray-400">
            Rubros: ${subtotalRubros.toFixed(2)} · Descuentos: -${totalDescuentos.toFixed(2)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-brand-dark">${neto.toFixed(2)}</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-4 space-y-3 pt-3">
          {/* Read-only rubros */}
          <div className="space-y-1">
            <p className="text-xs font-medium text-gray-500 uppercase">Rubros</p>
            {details.map((d) => (
              <div key={d.id} className="flex justify-between text-sm">
                <span className="text-gray-600 truncate">
                  {d.recipe_nombre}
                  {d.cabecera_nombre && (
                    <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{d.cabecera_nombre}</span>
                  )}
                  {' '}({d.cantidad} {d.unidad})
                </span>
                <span className="font-medium text-gray-700">${d.subtotal.toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Descuentos herramienta */}
          <div className="space-y-1 pt-2 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase">Descuentos herramienta</p>
            {descuentos
              .filter((d) => d.tipo === 'herramienta')
              .map((d) => (
                <DescuentoRow key={d.id} descuento={d} planillaId={planillaId} canDelete={isEditable} />
              ))}
            {descuentos.filter((d) => d.tipo === 'herramienta').length === 0 && (
              <p className="text-xs text-gray-400 italic">Sin descuentos de herramienta</p>
            )}
          </div>

          {/* Add discount form */}
          {isEditable && (
            <div className="pt-2">
              {showForm ? (
                <AddDescuentoForm
                  planillaId={planillaId}
                  maestroId={maestroId}
                  onClose={() => setShowForm(false)}
                />
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setShowForm(true)}>
                  <Plus className="w-3 h-3 mr-1" />
                  Agregar descuento herramienta
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Descuento Row ───────────────────────────────────────────────────

function DescuentoRow({ descuento, planillaId, canDelete }: { descuento: DescuentoDTO; planillaId: string; canDelete: boolean }) {
  const deleteDescuento = useDeleteDescuento(planillaId);

  const handleDelete = async () => {
    try {
      await deleteDescuento.mutateAsync(descuento.id);
      toast('success', 'Descuento eliminado');
    } catch {
      toast('error', 'Error al eliminar descuento');
    }
  };

  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-600 truncate flex-1">{descuento.descripcion}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-red-600">-${descuento.monto.toFixed(2)}</span>
        {canDelete && (
          <button onClick={handleDelete} className="text-gray-400 hover:text-red-500">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Add Descuento Form ──────────────────────────────────────────────

function AddDescuentoForm({
  planillaId,
  maestroId,
  onClose,
}: {
  planillaId: string;
  maestroId: string;
  onClose: () => void;
}) {
  const [descripcion, setDescripcion] = useState('');
  const [monto, setMonto] = useState('');
  const createDescuento = useCreateDescuento(planillaId);

  const handleSubmit = async () => {
    if (!descripcion.trim() || !monto || Number(monto) <= 0) {
      toast('error', 'Complete descripción y monto');
      return;
    }
    try {
      await createDescuento.mutateAsync({
        maestroId,
        tipo: 'herramienta',
        descripcion: descripcion.trim(),
        monto: Number(monto),
      });
      toast('success', 'Descuento agregado');
      onClose();
    } catch {
      toast('error', 'Error al agregar descuento');
    }
  };

  return (
    <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
      <Input
        label="Descripción"
        placeholder="Ej: Financiamiento andamio — cuota mensual"
        value={descripcion}
        onChange={(e) => setDescripcion(e.target.value)}
      />
      <Input
        label="Monto"
        type="number"
        placeholder="0.00"
        value={monto}
        onChange={(e) => setMonto(e.target.value)}
      />
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
        <Button size="sm" onClick={handleSubmit} isLoading={createDescuento.isPending}>Agregar</Button>
      </div>
    </div>
  );
}
